/**
 * FlowBlockNode - Block-specific node component
 * Sprint-8: Unified Entity Node Architecture
 *
 * Architecture:
 * - Uses BlockRenderCoordinator for rendering (delegates to BlockPreviewRenderer)
 * - Fetches block data from BlockRegistry using entityId only
 * - No direct rendering - fully delegates to coordinator
 * - Includes all data-testid attributes for E2E testing
 */

import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { NodeProps, NodeToolbar, Position } from '@xyflow/react';
import { Box, Button, Chip, Tooltip } from '@mui/material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { blockRenderCoordinator } from '@modules/reactFlow/services/block/BlockRenderCoordinator';
import type { NodeData } from '@modules/reactFlow/types/flow-canvas-types';
import { ConnectionInputHandle } from '@modules/reactFlow/components/shared/handles/ConnectionInputHandle';
import { ConnectionOutputHandle, DefaultOutputHandle } from '@modules/reactFlow/components/shared/handles/ConnectionOutputHandle';
import { BLOCK_DIMENSIONS } from '@modules/reactFlow/constants/blockDimensionConfig';
import { NodeActionToolbar } from '@modules/reactFlow/components/shared/NodeActionToolbar';
import { useAdaptingBlocks } from '@modules/reactFlow/contexts/AdaptingBlocksContext';
import { CircularProgress } from '@mui/material';
import { buildBlockActions } from '@modules/reactFlow/utils/actions/buildNodeActions';
import BlockNodeVariables from '@modules/reactFlow/components/block/BlockNodeVariables';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';
import { readLibraryAdaptationState } from '@modules/strategy-blocks/utils/libraryAdaptationState';

// Launch block indicator text
const LAUNCH_INDICATOR_TEXT = 'Launch Block →';

export interface FlowBlockNodeProps extends Omit<NodeProps, 'data'> {
  data: NodeData;
}

/**
 * FlowBlockNode - Block-specific node component
 *
 * Features:
 * - Uses BlockRenderCoordinator.renderById() for all rendering
 * - Fetches block from BlockRegistry via entityId (coordinator handles lookup)
 * - Data-testid attributes derived from coordinator data
 * - Theme-only styling (no custom CSS)
 * - Uses the shared coordinator-driven node architecture
 */
const FlowBlockNode: React.FC<FlowBlockNodeProps> = ({ data, selected }) => {
  const { entityId, handles = [] } = data;

  // Force update counter for registry changes
  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);

  // Subscribe to registry changes for this block
  useEffect(() => {
    const unsubscribe = blockRenderCoordinator.subscribe(entityId, () => {
      // Force re-render when block data changes in registry
      setForceUpdateCounter(c => c + 1);
    });

    return unsubscribe; // Cleanup on unmount
  }, [entityId]);

  // Fetch block from registry via coordinator
  const block = blockRenderCoordinator.getBlock(entityId);
  // AC-P1-069: adaptation-in-flight set from the editor's task listeners.
  const adaptingBlocks = useAdaptingBlocks();
  const libraryAdaptation = block ? readLibraryAdaptationState(block) : undefined;
  const isLibraryDraft = libraryAdaptation?.mode === 'staged'
    && libraryAdaptation.status !== 'adapted';
  const isAdapting = libraryAdaptation?.status === 'adapting'
    || Boolean(block?.id && adaptingBlocks.has(String(block.id)));
  const customSubtype = (block as any)?.customSubtype as string | undefined;
  const subtypeRecord = customSubtype ? data.blockSubtypesBySlug?.[customSubtype] : undefined;

  // Spec D4 (U11): the block's screens cite an OLDER insights analysis than
  // the strategy's pin (denormalized citedAnalysisIds — recomputed on every
  // screen save, so a completed rebase clears this on the block refresh).
  const citesOldAnalysis = useMemo(() => {
    const cited = (block as any)?.citedAnalysisIds;
    return Boolean(
      data.currentAnalysisId
      && Array.isArray(cited)
      && cited.some((id: unknown) => typeof id === 'string' && id !== data.currentAnalysisId),
    );
  }, [block, data.currentAnalysisId, forceUpdateCounter]);

  // Build action list based on block state (isLaunchBlock, stale citations)
  const actions = useMemo(() => {
    if (!block) return [];
    return buildBlockActions(block, { citesOldAnalysis });
  }, [block?.isLaunchBlock, block?.id, citesOldAnalysis, forceUpdateCounter]);

  // Handle action clicks
  const handleActionClick = useCallback(
    (entityId: string, actionKey: string) => {
      console.log('[FlowBlockNode] Action clicked:', { entityId, actionKey });

      // Call callback from data if provided
      if (data.onNodeAction && typeof data.onNodeAction === 'function') {
        data.onNodeAction(entityId, actionKey, 'block');
      }
    },
    [data.onNodeAction]
  );

  if (!block) {
    console.warn(`[FlowBlockNode] Block not found in registry: ${entityId}`);
    return (
      <Box
        data-testid={`node-${entityId}`}
        data-entity-type="block"
        sx={{
          width: BLOCK_DIMENSIONS.WIDTH,
          height: BLOCK_DIMENSIONS.HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed red',
          borderRadius: '8px',
          padding: 2,
        }}
      >
        Block not found
      </Box>
    );
  }

  return (
    <Box
      className={selected ? 'selected' : ''}
      data-testid={`node-${block.id}`}
      data-entity-id={block.entityId || block.id}
      data-block-type={block.blockType}
      data-block-name={block.name}
      data-node-x={block.position?.x}
      data-node-y={block.position?.y}
      data-entity-type="block"
      sx={{
        position: 'relative',
        width: BLOCK_DIMENSIONS.WIDTH,
        minHeight: BLOCK_DIMENSIONS.HEIGHT, // grows when an assigned-variables row is appended
        borderWidth: isLibraryDraft ? 2 : selected ? 1 : 2,
        borderStyle: isLibraryDraft ? 'dashed' : 'solid',
        borderColor: selected ? 'primary.main' : isLibraryDraft ? 'warning.main' : 'divider',
        borderRadius: '8px',
        backgroundColor: isLibraryDraft ? 'action.hover' : 'background.paper',
        boxShadow: selected ? 4 : 1,
        overflow: 'visible',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        userSelect: 'none',

        // Hover effect
        '&:hover': {
          boxShadow: 4,
        },

        // Animated selection glow (uses shared React Flow node keyframes)
        '&.selected::after': {
          content: '""',
          position: 'absolute',
          top: '-4px',
          left: '-4px',
          right: '-4px',
          bottom: '-4px',
          border: 2,
          borderColor: 'primary.main',
          borderRadius: '12px',
          pointerEvents: 'none',
          animation: 'selectionPulse 2s infinite',
        },
      }}
    >
      {/* Action Toolbar - shown when node is selected */}
      <NodeToolbar isVisible={selected} position={Position.Top} offset={10}>
        <NodeActionToolbar
          entityId={block.entityId || block.id!}
          entityName={block.name || 'Unnamed Block'}
          actions={actions}
          onActionClick={handleActionClick}
        />
      </NodeToolbar>

      {/* Connection Handles - reusable components */}
      {/* Input handle (target - left side) */}
      <ConnectionInputHandle entityId={block.entityId || block.id!} isLaunchScreen={block.isLaunchBlock || false} />

      {/* Delegate rendering to BlockRenderCoordinator. Body text scrolls
          ONLY on the selected node — hovering unselected nodes must keep the
          wheel on canvas zoom/pan (dev feedback 2026-08-05). */}
      {blockRenderCoordinator.renderById(entityId, {
        scale: 1,
        interactive: false,
        showTypeBadge: true,
        scrollableBody: Boolean(selected),
      })}

      {customSubtype && (
        <Tooltip
          title={subtypeRecord?.enabled === false
            ? 'This subtype is disabled for new assignments. The existing block keeps its pinned guidance.'
            : 'Project custom block subtype'}
          arrow
        >
          <Chip
            size="small"
            variant="outlined"
            color={subtypeRecord?.enabled === false ? 'warning' : 'primary'}
            label={`${subtypeRecord?.name ?? 'Unknown subtype'} · ${customSubtype}`}
            data-testid={STRATEGY_CANVAS.blockSubtypeBadge(block.id)}
            sx={{ mx: 1, mb: 1, maxWidth: 'calc(100% - 16px)' }}
          />
        </Tooltip>
      )}

      {isLibraryDraft && (
        <Tooltip title="A staged library example. Place and connect it, then review the exact strategy context before adapting and saving." arrow>
          <Chip
            size="small"
            variant="filled"
            color="warning"
            label="LIBRARY DRAFT"
            data-testid={STRATEGY_CANVAS.libraryDraftBadge(block.id)}
            sx={{ mx: 1, mb: 0.75, alignSelf: 'flex-start', fontWeight: 700 }}
          />
        </Tooltip>
      )}

      {isLibraryDraft && libraryAdaptation && (
        <Tooltip
          title={libraryAdaptation.error?.message
            ?? (isAdapting
              ? 'The AI is adapting this draft. Nothing is saved until the complete block passes validation.'
              : 'Select the node to review placement, evidence, variables, theme, and model.')}
          arrow
        >
          <Chip
            size="small"
            variant="outlined"
            color={libraryAdaptation.status === 'failed'
              ? 'error'
              : libraryAdaptation.status === 'adapting'
                ? 'info'
                : 'warning'}
            icon={isAdapting ? <CircularProgress size={12} /> : undefined}
            label={libraryAdaptation.status === 'failed'
              ? 'adaptation failed'
              : libraryAdaptation.status === 'stale'
                ? 'context changed'
                : isAdapting
                  ? 'AI adapting…'
                  : 'connect, review & adapt'}
            data-testid={STRATEGY_CANVAS.libraryDraftState(block.id)}
            sx={{ mx: 1, mb: 1, alignSelf: 'flex-start', backgroundColor: 'background.paper' }}
          />
        </Tooltip>
      )}

      {/* C7-4 (SI-9/AC-D01): variant provenance chip — the block is a sibling
          variant of another block, generated by the clone-variants fan-out */}
      {(block as { aiMeta?: { variantOf?: string } }).aiMeta?.variantOf && (
        <Tooltip title="Generated as a variant of another block — compare and keep the winner." arrow>
          <Chip
            size="small"
            variant="outlined"
            color="secondary"
            label="variant"
            data-testid={`block-variant-of-${(block as { aiMeta?: { variantOf?: string } }).aiMeta?.variantOf}`}
            sx={{ mx: 1, mb: 1, alignSelf: 'flex-start' }}
          />
        </Tooltip>
      )}

      {/* Assigned-variables row — integrated into the card, shown whenever the block collects variables */}
      <BlockNodeVariables
        assignments={(block as any).blockContract?.outputAssignment}
        variablesById={data.variablesById}
      />

      {/* Spec D4 (U11): stale-citation warning — cleared automatically once a
          rebase regen recomputes the block's citedAnalysisIds */}
      {citesOldAnalysis && (
        <Tooltip
          title="This block's screens cite an older insights analysis than the strategy's current one. Rebase re-grounds the data and keeps the block's structure and creative approach."
          arrow
        >
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            icon={<WarningAmberOutlined />}
            label="cites old analysis"
            data-testid={`block-cites-old-${block.id}`}
            sx={{ position: 'absolute', bottom: 8, left: 8, zIndex: 10, backgroundColor: 'background.paper' }}
          />
        </Tooltip>
      )}

      {/* "Create with AI" button — shown when block has no screens */}
      {!block.firstScreenId && !isLibraryDraft && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AutoAwesome />}
          data-testid={STRATEGY_CANVAS.createBlockScreens(block.id)}
          onClick={(e) => {
            e.stopPropagation();
            handleActionClick(block.entityId || block.id!, 'generate-screens');
          }}
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '11px',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            zIndex: 10,
            backgroundColor: 'background.paper',
            borderColor: 'primary.main',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          Create with AI
        </Button>
      )}

      {/* Output handles (source - right side) */}
      {handles && handles.length > 0 ? (
        handles.map((handle, index) => (
          <ConnectionOutputHandle
            key={handle.id}
            entityId={block.entityId || block.id!}
            handle={handle}
            index={index}
          />
        ))
      ) : (
        // Default handle if no handles provided
        <DefaultOutputHandle entityId={block.entityId || block.id!} entityType="block" />
      )}

      {/* Launch block indicator */}
      {block.isLaunchBlock && (
        <div
          className="launch-indicator"
          data-testid={`launch-indicator-${block.id}`}
          style={{
            position: 'absolute',
            left: '-100px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(25, 118, 210, 0.1)',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#1976d2',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {LAUNCH_INDICATOR_TEXT}
        </div>
      )}
    </Box>
  );
};

export default FlowBlockNode;
